import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiStar,
  FiSearch,
  FiFilter,
  FiZap,
  FiTruck,
  FiCheck,
  FiMail,
  FiPhone,
  FiMapPin,
  FiExternalLink,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { Card, Button, Loading } from "../components/common";
import Navbar from "../components/Navbar";
import { CompanySelectModal } from "../components/modals";
import { useTenant } from "../context/TenantContext";
import { getCarsByCompany } from "../firebase/cars";

const Home = () => {
  const navigate = useNavigate();
  const {
    company,
    companyId,
    companies,
    isMainSite,
    isLoading: tenantLoading,
    error: tenantError,
    setTenantManually,
  } = useTenant();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFuelType, setSelectedFuelType] = useState("all");
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null); // Track locally selected company

  // Determine which company ID to use (from tenant context or local selection)
  const activeCompanyId = companyId || selectedCompany?.id;
  const activeCompany = company || selectedCompany;

  // Load cars only on company subdomain (not on main site)
  useEffect(() => {
    if (tenantLoading) return;

    if (isMainSite) {
      // On main site - show platform info and open company modal
      setLoading(false);
      setCars([]);
      // Auto-open modal on main site
      if (companies && companies.length > 0) {
        setShowCompanyModal(true);
      }
    } else if (activeCompanyId) {
      // Company subdomain - load cars
      loadCompanyCars(activeCompanyId);
    } else {
      // No tenant context - show empty state
      setCars([]);
      setLoading(false);
    }
  }, [activeCompanyId, isMainSite, tenantLoading, companies]);

  const loadCompanyCars = async (companyIdToLoad) => {
    setLoading(true);
    try {
      const result = await getCarsByCompany(companyIdToLoad);
      if (result.cars) {
        // Only show company's cars from Firebase - no static fallback
        setCars(result.cars);
      } else {
        // If error or no cars, show empty state
        setCars([]);
      }
    } catch (error) {
      console.error("Error loading cars:", error);
      // Show empty state on error - don't mix static demo data
      setCars([]);
    } finally {
      setLoading(false);
    }
  };

  const getCompanyUrl = (slug) => {
    const hostname = window.location.hostname;
    const port = window.location.port;

    if (hostname === "localhost" || hostname.endsWith(".localhost")) {
      return `http://${slug}.localhost:${port || "3000"}`;
    }

    // Detect the base domain from current hostname
    const baseDomain = hostname.includes("satyasreeman.com")
      ? "satyasreeman.com"
      : hostname.includes("rentsacar.com")
        ? "rentsacar.com"
        : "carbooking.com";

    return `https://${slug}.${baseDomain}`;
  };

  // Handle company selection from modal
  const handleCompanySelect = (selectedCompanyData) => {
    if (!selectedCompanyData?.slug) {
      console.error("Invalid company data:", selectedCompanyData);
      return;
    }

    // Navigate immediately to the company's subdomain
    // This will cause a full page reload, so no need to update state
    const targetUrl = getCompanyUrl(selectedCompanyData.slug);

    // Use window.location.replace to ensure navigation happens
    // and prevent back button from going to the modal state
    window.location.replace(targetUrl);
  };

  // Get unique fuel types from loaded cars
  const fuelTypes = [
    "all",
    ...new Set(cars.map((car) => car.fuelType).filter(Boolean)),
  ];

  const filteredCars = cars.filter((car) => {
    const matchesSearch =
      car.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      car.brand?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFuel =
      selectedFuelType === "all" || car.fuelType === selectedFuelType;
    const isAvailable =
      car.available !== false &&
      car.status !== "inactive" &&
      car.status !== "maintenance";

    return matchesSearch && matchesFuel && isAvailable;
  });

  const handleCarClick = (carId) => {
    navigate(`/car/${carId}`);
  };

  if (tenantLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loading />
        </div>
      </div>
    );
  }

  if (tenantError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="text-6xl mb-4">😕</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Company Not Found
            </h2>
            <p className="text-gray-600 mb-4">{tenantError}</p>
            <a
              href={`http://localhost:${window.location.port || "3000"}`}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Go to main site
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      {/* Company Selection Modal - Show on main site */}
      <CompanySelectModal
        companies={companies}
        isOpen={showCompanyModal && isMainSite}
        onClose={() => setShowCompanyModal(false)}
        onSelect={handleCompanySelect}
      />

      {/* Main Site - Platform Landing Page */}
      {isMainSite ? (
        <>
          {/* Hero Section */}
          <section className="relative bg-gradient-to-r from-primary-600 via-primary-500 to-primary-600 text-white overflow-hidden">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
              <div className="text-center">
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 px-2">
                  Your Car Rental Platform Solution
                </h2>
                <p className="text-base sm:text-lg lg:text-xl text-primary-100 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
                  Transform your car rental business with a complete booking
                  platform. Get your own branded website and manage bookings
                  effortlessly.
                </p>
                <Button
                  onClick={() => setShowCompanyModal(true)}
                  className="bg-white text-primary-600 hover:bg-primary-50"
                >
                  View Our Partners
                </Button>
              </div>
            </div>
          </section>

          {/* Platform Information */}
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="text-center mb-12">
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Why Choose Our Platform?
              </h3>
              <p className="text-gray-600 max-w-2xl mx-auto">
                We provide a complete solution for car rental businesses to
                establish their online presence and manage operations
                efficiently.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 sm:gap-8 mb-12">
              <Card className="p-6 text-center border-0 shadow-soft">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiTruck className="text-primary-600" size={32} />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Your Own Branded Website
                </h4>
                <p className="text-gray-600 text-sm">
                  Get a custom subdomain with your company branding and logo.
                </p>
              </Card>

              <Card className="p-6 text-center border-0 shadow-soft">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiCheck className="text-primary-600" size={32} />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Easy Booking Management
                </h4>
                <p className="text-gray-600 text-sm">
                  Manage all your bookings, cars, and customers from one
                  dashboard.
                </p>
              </Card>

              <Card className="p-6 text-center border-0 shadow-soft">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiZap className="text-primary-600" size={32} />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Quick Setup
                </h4>
                <p className="text-gray-600 text-sm">
                  Get started in minutes. Add your cars and start accepting
                  bookings.
                </p>
              </Card>
            </div>
          </section>

          {/* Onboarding Process */}
          <section className="bg-gray-50 py-12 sm:py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                  How It Works
                </h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Simple onboarding process to get your business online
                </p>
              </div>

              <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
                <div className="text-center">
                  <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                    1
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Contact Us
                  </h4>
                  <p className="text-sm text-gray-600">
                    Reach out to discuss your requirements
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                    2
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Company Setup
                  </h4>
                  <p className="text-sm text-gray-600">
                    We'll create your company profile and subdomain
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                    3
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Add Your Cars
                  </h4>
                  <p className="text-sm text-gray-600">
                    Upload your fleet details and pricing
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                    4
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Go Live</h4>
                  <p className="text-sm text-gray-600">
                    Start accepting bookings from customers
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <Card className="p-8 sm:p-12 text-center border-0 shadow-soft">
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Ready to Get Started?
              </h3>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Contact us to learn more about our platform and how we can help
                grow your car rental business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href="mailto:hr@anddhen.com"
                  className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
                >
                  <FiMail size={20} />
                  <span>hr@anddhen.com</span>
                </a>
                <span className="hidden sm:inline text-gray-300">|</span>
                <a
                  href="mailto:anddhenconsulting@gmail.com"
                  className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
                >
                  <FiMail size={20} />
                  <span>anddhenconsulting@gmail.com</span>
                </a>
                <span className="hidden sm:inline text-gray-300">|</span>
                <a
                  href="tel:+919110736115"
                  className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
                >
                  <FiPhone size={20} />
                  <span>+91 9110736115</span>
                </a>
                <span className="hidden sm:inline text-gray-300">|</span>
                <a
                  href="https://wa.me/919110736115"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-green-600 hover:text-green-700 font-medium"
                >
                  <FaWhatsapp size={20} />
                  <span>WhatsApp</span>
                </a>
              </div>
            </Card>
          </section>

          {/* Existing Companies */}
          {companies && companies.length > 0 && (
            <section className="bg-gray-50 py-12 sm:py-16">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                    Our Partners
                  </h3>
                  <p className="text-gray-600">
                    Car rental companies using our platform
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {companies.slice(0, 6).map((company) => (
                    <Card
                      key={company.id}
                      hover
                      onClick={() => handleCompanySelect(company)}
                      className="p-6 border-0 shadow-soft card-hover cursor-pointer"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                          {company.logo ? (
                            <img
                              src={company.logo}
                              alt={company.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-2xl font-bold text-gray-400">
                              {company.name[0]}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {company.name}
                          </h4>
                          {company.location && (
                            <p className="text-sm text-gray-600 flex items-center gap-1">
                              <FiMapPin size={14} />
                              {company.location}
                            </p>
                          )}
                          <div className="mt-3 flex items-center gap-2 text-primary-600 text-sm font-medium">
                            <span>Visit Website</span>
                            <FiExternalLink size={14} />
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>

                {companies.length > 6 && (
                  <div className="text-center mt-8">
                    <Button
                      onClick={() => setShowCompanyModal(true)}
                      variant="outline"
                    >
                      View All Companies ({companies.length})
                    </Button>
                  </div>
                )}
              </div>
            </section>
          )}
        </>
      ) : (
        <>
          {/* Company Subdomain - Show Cars */}
          {/* Hero Section */}
          <section className="relative bg-gradient-to-r from-primary-600 via-primary-500 to-primary-600 text-white overflow-hidden">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
              <div className="text-center">
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 px-2">
                  {activeCompany
                    ? `Welcome to ${activeCompany.name}`
                    : "Find Your Perfect Ride"}
                </h2>
                <p className="text-base sm:text-lg lg:text-xl text-primary-100 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
                  Book premium cars at affordable prices. Experience comfort and
                  style on every journey.
                </p>
              </div>
            </div>
          </section>

          {/* Search and Filters */}
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 sm:-mt-8 relative z-10">
            <Card className="p-4 sm:p-6 shadow-medium border-0">
              <div className="space-y-3 sm:space-y-4">
                {/* Search Bar */}
                <div className="relative">
                  <FiSearch className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                  <input
                    type="text"
                    placeholder="Search by car name or brand..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400 text-sm sm:text-base"
                  />
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
                  <div className="flex items-center gap-1.5 sm:gap-2 text-gray-600">
                    <FiFilter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="text-xs sm:text-sm font-medium">
                      Filters:
                    </span>
                  </div>

                  <select
                    value={selectedFuelType}
                    onChange={(e) => setSelectedFuelType(e.target.value)}
                    className="px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-xs sm:text-sm font-medium text-gray-700 cursor-pointer hover:border-primary-300 transition-colors flex-1 sm:flex-initial"
                  >
                    {fuelTypes.map((fuel) => (
                      <option key={fuel} value={fuel}>
                        {fuel === "all" ? "⛽ All Fuel Types" : `⛽ ${fuel}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Card>
          </section>

          {/* Cars Grid */}
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
            {filteredCars.length === 0 ? (
              <div className="text-center py-12 sm:py-16">
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiTruck className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                  No cars found
                </h3>
                <p className="text-sm sm:text-base text-gray-600">
                  Try adjusting your search or filters
                </p>
              </div>
            ) : (
              <>
                <div className="mb-6 sm:mb-8">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">
                    Available Cars
                  </h2>
                  <p className="text-sm sm:text-base text-gray-600">
                    {filteredCars.length} car
                    {filteredCars.length !== 1 ? "s" : ""} available
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {filteredCars.map((car) => (
                    <Card
                      key={car.id}
                      hover
                      onClick={() => handleCarClick(car.id)}
                      className="overflow-hidden border-0 shadow-soft card-hover"
                    >
                      <div className="relative">
                        <img
                          src={car.images?.[0] || car.image}
                          alt={car.name}
                          className="w-full h-48 sm:h-56 object-contain bg-gray-100"
                        />
                        {car.rating !== undefined &&
                          car.rating !== null &&
                          Number(car.rating) > 0 && (
                            <div className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-lg flex items-center gap-1 sm:gap-1.5 shadow-lg border border-yellow-300">
                              <FiStar className="fill-white text-white w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              <span className="text-xs sm:text-sm font-bold">
                                {Number(car.rating).toFixed(1)}
                              </span>
                            </div>
                          )}
                        {car.fuelType === "Electric" && (
                          <div className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-primary-500 text-white px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-lg text-xs font-semibold flex items-center gap-1">
                            <FiZap className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                            Electric
                          </div>
                        )}
                      </div>

                      <div className="p-4 sm:p-5">
                        <div className="mb-2 sm:mb-3">
                          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                            {car.name}
                          </h3>
                          <p className="text-xs sm:text-sm text-gray-600">
                            {car.brand} {car.model && `• ${car.model}`}
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                          <span className="px-2 sm:px-2.5 py-0.5 sm:py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg">
                            {car.seats} Seats
                          </span>
                          <span className="px-2 sm:px-2.5 py-0.5 sm:py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg">
                            {car.transmission}
                          </span>
                          {car.mileage && (
                            <span className="px-2 sm:px-2.5 py-0.5 sm:py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg">
                              {car.mileage}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 pt-3 sm:pt-4 border-t border-gray-100">
                          <div>
                            <p className="text-xl sm:text-2xl font-bold text-primary-600">
                              ₹{(car.price12Hour || 0).toLocaleString()}
                            </p>
                            <p className="text-xs text-gray-500">
                              per 12-hour slot
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              ₹{(car.price24Hour || 0).toLocaleString()} / 24hrs
                            </p>
                          </div>
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCarClick(car.id);
                            }}
                            className="gradient-primary text-white w-full sm:w-auto"
                          >
                            Book Now
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </main>
        </>
      )}

      {/* Company Info Section - Show only on company subdomain */}
      {!isMainSite && activeCompany && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <Card className="p-6 sm:p-8 bg-gray-50">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  About Us
                </h3>
                <p className="text-gray-600 mb-4 whitespace-pre-wrap">
                  {activeCompany.description ||
                    `${activeCompany.name} is your trusted partner for car rentals.`}
                </p>
                {activeCompany.serviceAreas?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Service Areas:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {activeCompany.serviceAreas.map((area, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-white rounded-full text-sm text-gray-600"
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Contact Us
                </h3>
                <div className="space-y-3">
                  {activeCompany.phone && (
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500">📞</span>
                      <a
                        href={`tel:${activeCompany.phone}`}
                        className="text-gray-700 hover:text-primary-600"
                      >
                        {activeCompany.phone}
                      </a>
                    </div>
                  )}
                  {activeCompany.email && (
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500">✉️</span>
                      <a
                        href={`mailto:${activeCompany.email}`}
                        className="text-gray-700 hover:text-primary-600"
                      >
                        {activeCompany.email}
                      </a>
                    </div>
                  )}
                  {activeCompany.whatsapp && (
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500">💬</span>
                      <a
                        href={`https://wa.me/${activeCompany.whatsapp.replace(/[^0-9]/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-700 hover:text-primary-600"
                      >
                        WhatsApp: {activeCompany.whatsapp}
                      </a>
                    </div>
                  )}
                  {activeCompany.operatingHours && (
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500">🕐</span>
                      <span className="text-gray-700">
                        {activeCompany.operatingHours.open} -{" "}
                        {activeCompany.operatingHours.close}
                        {activeCompany.operatingHours.closedDays?.length >
                          0 && (
                          <span className="text-sm text-gray-500 ml-2">
                            (Closed:{" "}
                            {activeCompany.operatingHours.closedDays.join(", ")}
                            )
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white mt-12 sm:mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="text-center text-sm text-gray-400">
            <p>
              &copy; 2026{" "}
              {isMainSite
                ? "CarBooking Platform"
                : activeCompany?.name || "CarBooking"}
              . All rights reserved.
            </p>
            <p className="mt-2">DEVELOPED BY ANDDHEN GROUP</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
